import _ from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { notificationError } from 'core/utils';
import { BaseViewStore } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { OrderStore, FulfillmentOrderStore, FulfillmentOrderProcessingStore } from 'modules/order/stores';
import { EditOrderForm } from 'modules/order/forms';
import moment from 'moment';
import { AddressAutofillViewStore, BaasicDropdownViewStore } from 'common/stores';
import isShippingStateAvailable from 'core/utils/isShippingStateAvailable';
import { AddressValidationTemplate } from 'themes/modules/order/pages';
import { Replacement } from '../pages';

class EditOrderViewStore extends BaseViewStore {
	@observable useEnteredAddress = true;
	addressValidationResponse = null;
	isAddressVerified = false;
	selectedItem = null;
	isReplacement = false;
	form = new EditOrderForm({
		onSuccess: async () => {
			await this.editOrder();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.lookupStore = new LookupStore(rootStore);
		this.orderStore = new OrderStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.initialize(modalParams.data.selectedItemId);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		this.stateDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.state,
			field: this.form.$('shippingCoreCountryStateId'),
			store: this.lookupStore,
		});
		this.countryDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.country,
			field: this.form.$('shippingCoreCountryId'),
			store: this.lookupStore,
			onChangeCallback: value => this.handleShippingState(value),
		});
		this.addressAutofillViewStore = new AddressAutofillViewStore(this.rootStore, {
			parentViewData: {
				buyerName: this.form.$('buyerName'),
				address1: this.form.$('shippingAddress1'),
				address2: this.form.$('shippingAddress2'),
				city: this.form.$('shippingCity'),
				zipCode: this.form.$('shippingZipCode'),
				isBaasicDropdown: true,
				stateDropdownStore: this.stateDropdownStore,
				countryDropdownStore: this.countryDropdownStore,
			},
		});
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');

		if (this.modalParams.data.parentView.constructor.name === 'ReplacementViewStore') {
			this.selectedItem = this.modalParams.data.parentView.selectedOrder;
			this.isReplacement = true;
		} else {
			await this.getFulfillmentOrder(selectedItemId);
		}

		this.setFormValues();
		this.resume();
	};

	@action.bound
	handleShippingState = country => {
		if (!country || !isShippingStateAvailable(country.extra.countryCode)) {
			this.form.$('shippingCoreCountryStateId').set('rules', '');
			this.stateDropdownStore.setValue(null);
		} else {
			this.form.$('shippingCoreCountryStateId').set('rules', 'required');
		}
	};

	@action.bound
	handleUpdate = async orderDetails => {
		if (this.isReplacement) {
			this.openModal({
				onSuccess: this.modalParams.data.parentViewCallback,
				data: {
					parentView: { ...this, orderDetails },
					selectedItemId: this.modalParams.data.selectedItemId,
				},
				component: Replacement,
			});
		} else {
			await this.fulfillmentOrderProcessingStore.updateFulfillmentOrderInfo(
				orderDetails,
				sourceOfChange.updateFulfillmentOrderInfo
			);
			this.rootStore.notificationStore.success(['FULFILLMENTORDER.EDITMODAL.SUCCESS']);
			this.rootStore.viewStore.selectedModal.success();
		}
	};

	@action.bound
	validateAddress = async orderDetails => {
		return this.isReplacement
			? await this.orderStore.validateOrderAddress(orderDetails)
			: await this.fulfillmentOrderStore.validateOrderAddress(orderDetails);
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: [
				'shippingCoreCountryState',
				'shippingCoreCountry',
				'order',
				'order.shippingCoreCountryState',
				'order.shippingCoreCountry',
			],
		});
	};

	@action.bound
	editOrder = async (shouldValidate = true) => {
		this.suspend();
		const orderDetails = {
			...this.form.values(),
			id: this.selectedItem.id,
			orderId: this.selectedItem.orderId,
			dateFormat: this.datePickerFormat.toLowerCase().replaceAll('m', 'M'),
			addressChanged: !this.useEnteredAddress,
			isAddressVerified: this.isAddressVerified,
		};
		try {
			if (shouldValidate) {
				this.addressValidationResponse = await this.validateAddress(orderDetails);
				this.isAddressVerified = this.addressValidationResponse.isAddressVerified;
				orderDetails.isAddressVerified = this.addressValidationResponse.isAddressVerified;
				orderDetails.addressChanged = this.addressValidationResponse.addressChanged;

				// If Postgrid updated the address.
				if (this.addressValidationResponse.updatedAddress != null) {
					this.openModal({
						data: this,
						component: AddressValidationTemplate,
					});
					return;
				}
			}
			if (!this.useEnteredAddress) {
				orderDetails.shippingAddress1 = this.addressValidationResponse.updatedAddress.streetLines[0]
					? this.addressValidationResponse.updatedAddress.streetLines[0]
					: '';
				orderDetails.shippingAddress2 = this.addressValidationResponse.updatedAddress.streetLines[1]
					? this.addressValidationResponse.updatedAddress.streetLines[1]
					: '';
				orderDetails.shippingCity = this.addressValidationResponse.updatedAddress.city;
				orderDetails.shippingZipCode = this.addressValidationResponse.updatedAddress.postalCode;
			}

			await this.handleUpdate(orderDetails);
			this.modalParams.success();
		} catch (err) {
			if (err.statusCode == 400 && _.get(err, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	setFormValues = () => {
		//If there is any address data info on FO then used those data
		const formItem = this.selectedItem;
		formItem.shipByDate = moment(this.selectedItem.shipByDate)
			.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
			.format(this.datePickerFormat);
		this.form.updateForm(formItem, false);
		if (formItem.shippingCoreCountryState) {
			this.stateDropdownStore.setValue({
				value: formItem.shippingCoreCountryState.id,
				label: formItem.shippingCoreCountryState.name,
				extra: formItem.shippingCoreCountryState,
			});
		} else {
			this.handleShippingState(null);
		}
		if (formItem.shippingCoreCountry) {
			this.countryDropdownStore.setValue({
				value: formItem.shippingCoreCountry.id,
				label: formItem.shippingCoreCountry.name,
				extra: formItem.shippingCoreCountry,
			});
		}
	};

	@action.bound
	async onValidateAddressBack() {
		this.useEnteredAddress = true;
		this.openModal(this.modalParams.data.parentView.getEditOrderModalProps(this.selectedItem));
	}

	@action.bound
	onToggleAddress(useEnteredAddress) {
		this.useEnteredAddress = useEnteredAddress;
	}

	@action.bound
	async onValidateAddressConfirm() {
		await this.editOrder(false);
	}
}

export default EditOrderViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/EditOrderViewStore.js